import type { LinearRGB } from '../../../core/color';
import type { Landmark } from '../../../data/types';
import type { Ring, Vec2 } from '../../../geo/types';
import { PATTERN } from '../../../render/patterns/patternIds';
import { SURFACE } from '../../mesh/attributes';
import type { Frame, MeshBuilder } from '../../mesh/meshBuilder';
import { gableRoof } from '../roofSolids';
import { horizontalCap, orientedFace, planPoint, pyramidOverRing, ringPoints } from '../solids';
import { buildConventRange } from './conventRange';
import { buildConventSoil } from './conventSoil';
import { corners } from './genericBlock';
import type { LandmarkContext } from './landmarkFeature';
import { centredWindow, gableWall, pointedWindows, rectWall, type MasonryStyles } from './masonry';
import { paintedChapelStyles, type PaintedChapelColors } from './paintedChapels';
import { meshFrameOf } from './planFrame';
import {
  sintLucienFloors,
  sintLucienFrame,
  sintLucienRangeFrame,
  sintLucienReading,
} from './sintLucienPlan';
import { NO_TRACERY, type TracerySink } from './tracery/tracerySink';

type ChapelPart = ReturnType<typeof sintLucienReading>['chapel'][number];

function chapelPart(builder: MeshBuilder, frame: Frame, brick: MasonryStyles, p: ChapelPart) {
  const width = p.v1 - p.v0,
    height = { eaveY: p.eaves, apexY: p.ridge },
    portal = p.entrance ? centredWindow(width, 0, 3.3, 0.2) : [],
    front: readonly [Vec2, Vec2] = [
      [p.u1, p.v0],
      [p.u1, p.v1],
    ];
  gableWall(builder, brick, frame, front, height, [
    ...portal,
    ...(p.entrance ? centredWindow(width, 5.8, 10.6, 0.35) : []),
  ]);
  // The doorway is closed; neither source supplies a walkable chapel interior.
  for (const ring of portal) {
    const wood = { ...brick.opening, surface: SURFACE.wood, pattern: PATTERN.timberFront1538 },
      face = ring.map((q) => planPoint(p.u1 - 0.13, q[1], p.v0 + q[0])),
      back = ring.map((q) => planPoint(p.u1 - 0.18, q[1], p.v0 + q[0]));
    orientedFace(builder, wood, frame, face, [1, 0, 0]);
    orientedFace(builder, wood, frame, back, [-1, 0, 0]);
    ring.forEach((q, i) => {
      const next = (i + 1) % ring.length,
        r = ring[next]!;
      orientedFace(
        builder,
        wood,
        frame,
        [face[i]!, face[next]!, back[next]!, back[i]!],
        [0, q[0] - r[0], q[1] - r[1]]
      );
    });
  }
  gableWall(
    builder,
    brick,
    frame,
    [
      [p.u0, p.v1],
      [p.u0, p.v0],
    ],
    height,
    p.entrance ? [] : centredWindow(width, 5.8, 10.6, 0.35)
  );
  // The priest's house masks the northern flank. The lower and upper bands are estimates.
  const lower: Ring[] = pointedWindows(p.u1 - p.u0, 5.5, 1.3, 3.6),
    upper = pointedWindows(p.u1 - p.u0, 5.5, 6, 10.5);
  rectWall(
    builder,
    brick,
    frame,
    [
      [p.u0, p.v0],
      [p.u1, p.v0],
    ],
    [0, p.eaves],
    [...lower, ...upper]
  );
  rectWall(
    builder,
    brick,
    frame,
    [
      [p.u1, p.v1],
      [p.u0, p.v1],
    ],
    [0, p.eaves]
  );
  horizontalCap(builder, brick.wall, frame, corners(p), 0.01, true);
  gableRoof(builder, brick, frame, {
    ...p,
    eaveY: p.eaves,
    ridgeY: p.ridge,
    ends: { start: false, end: false },
  });
}

/** Luciën's non-oriented chapel, parallel priest's house and segmented canal wing. */
export function buildSintLucien(
  builder: MeshBuilder,
  landmark: Landmark,
  colors: PaintedChapelColors & { tile: LinearRGB; ground: LinearRGB; garden: LinearRGB },
  ids: { readonly edgeId: number; readonly seed: number },
  tracery: TracerySink = NO_TRACERY,
  context: Pick<LandmarkContext, 'elevation' | 'elevationAt'> = {}
): void {
  const plan = sintLucienFrame(landmark),
    frame = meshFrameOf(plan),
    reading = sintLucienReading(landmark),
    styles = paintedChapelStyles(colors, ids, tracery),
    brick = { ...styles, wall: { ...styles.wall, surface: SURFACE.brick } };
  for (const p of reading.chapel) chapelPart(builder, frame, brick, p);
  const bell = reading.belfry,
    ring = ringPoints([bell.u, bell.v], bell.radius, 8, Math.PI / 8);
  ring.forEach((from, i) => {
    const to = ring[(i + 1) % ring.length]!,
      width = Math.hypot(to[0] - from[0], to[1] - from[1]);
    rectWall(
      builder,
      brick,
      frame,
      [from, to],
      [bell.foot, bell.eaves],
      centredWindow(width, 1.6, bell.eaves - bell.foot - 0.3, 0.5),
      bell.foot
    );
  });
  horizontalCap(builder, brick.wall, frame, ring, bell.foot, false);
  pyramidOverRing(builder, brick, frame, ring, bell.eaves, { plan: [bell.u, bell.v], y: bell.tip });
  for (const p of reading.ranges)
    buildConventRange(
      builder,
      meshFrameOf(sintLucienRangeFrame(plan, p)),
      p.roof === 'tile'
        ? {
            ...brick,
            roof: {
              ...brick.roof,
              color: colors.tile,
              surface: SURFACE.roofTile,
              pattern: PATTERN.roofTileHollePan,
            },
          }
        : brick,
      p
    );
  const base = context.elevation ?? 0,
    terrain = context.elevationAt ?? (() => base);
  for (const floor of sintLucienFloors(landmark))
    buildConventSoil(
      builder,
      { ...brick.wall, color: floor.garden ? colors.garden : colors.ground },
      floor.ring,
      terrain,
      base,
      floor.height,
      floor.garden
    );
}
